<template>
	<SidePanelContent class="w-[30rem]" hide-on-click-outside>
		<template #heading>{{ t('saleDaysSummaryPanel.title') }}</template>

		<div class="font-body-s mb-05 mt-03 text-ink-60">
			{{ t('saleDaysSummaryPanel.heading') }}
		</div>

		<div class="flex items-center justify-between rounded-sm border border-ink-12 bg-ink-inverted-12 p-04">
			<div class="font-body-s text-ink-87">{{ t('saleDaysSummaryPanel.sellMoreGames') }}</div>

			<Button size="xs" variant="secondary" @click="openRevenueUpsellModal">{{ t('saleDaysSummaryPanel.unlockRevenue') }}</Button>
		</div>

		<div :class="['my-05 flex', { 'pointer-events-none opacity-50': isLoading }]">
			<Dropdown
				dropdown-id="scorePeriods"
				placeholder=""
				:data="periods"
				:show-search-row="false"
				name="scorePeriods"
				single
				@selected:data="onPeriodChange"
			/>
		</div>

		<SaleDaysSummary v-if="summary && selectedPeriod" :data="summary" :is-loading="isLoading" :period="selectedPeriod" />

		<template v-if="hasStoresWithoutFavouriteSKUs" #pre-footer>
			<div class="font-caption flex items-center justify-center whitespace-pre-line bg-message-warning p-03 text-ink-inverted-87">
				<T path="saleDaysSummaryPanel.missingStoresInfo">
					<template #storesWithSummaryNames>
						{{ storesWithSummaryNames }}
					</template>
				</T>
			</div>
		</template>

		<template #footer>
			<div class="flex justify-end">
				<Button size="s" @click="closeSaleDaysSummary()"> {{ t('saleDaysSummaryPanel.close') }}</Button>
			</div>
		</template>
	</SidePanelContent>
</template>

<script setup lang="ts">
import { Button, Dropdown, useTranslation } from '@bluebrick/saas-ui'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import SaleDaysSummary from '@/features/saleDaysSummary/components/SaleDaysSummary.vue'
import { useSaleDaysSummaryStore } from '@/features/saleDaysSummary/useSaleDaysSummaryStore'
import SidePanelContent from '@/features/sidePanel/components/SidePanelContent.vue'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { useUpsellStore } from '@/features/upsell/useUpsellStore'
import T from '@/translations/T.vue'

const { t } = useTranslation()
const { closeSaleDaysSummary } = useSidebarStore()
const { summary, isLoading, periods, selectedPeriod, storesWithSummaryIds } = storeToRefs(useSaleDaysSummaryStore())
const { onPeriodChange } = useSaleDaysSummaryStore()
const { favouriteSKUIds, favouriteSKUIdsByStore } = storeToRefs(useSkusStore())
const { storeIdToStoreName } = storeToRefs(useStoresStore())
const { openRevenueUpsellModal } = useUpsellStore()

const hasStoresWithoutFavouriteSKUs = computed(() => {
	if (favouriteSKUIds.value.length === 0) {
		// If no favourite SKUs are selected then it means that all SKUs from all stores are selected
		return false
	}

	// Return true if any store for which summary can be calculated (Steam and Nintendo*) does not
	// have any favourite SKUs
	return storesWithSummaryIds.value.some((store) => !favouriteSKUIdsByStore.value.get(store)?.length)
})

const storesWithSummaryNames = computed(() => storesWithSummaryIds.value.map((store) => storeIdToStoreName.value[store] ?? store).join(', '))
</script>
